import React, { useEffect, useState } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "@/components/icons";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState, EmptyState } from "@/components/ListState";
import { WerkbonStatusBadge, formatNlDate, formatEuro } from "@/components/Badges";
import { formatUren } from "@/lib/uren";
import { plural } from "@/lib/plural";
import { Code } from "@/components/Code";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

// Ruling 6, made visible: every werkbon that carries hours and is switched off
// or cancelled. One unticked box in March must not be invisible in December —
// and cancelling never hides hours (the 38,25-hour McMain finding).
export default function WerkbonnenUitgezet() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get("/werkbonnen/uitgezet"); setRows(data); }
    catch (e) { setError(getErrorMessage(e, "Kan het overzicht niet laden.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totalUren = rows.reduce((n, r) => n + (r.uren_totaal || 0), 0);
  const facts = loading ? ["laden…"] : [
    plural(rows.length, "bon met uren", "bonnen met uren"),
    ...(totalUren > 0 ? [`${formatUren(totalUren)} uur op het spel`] : []),
  ];

  // §7b.3: a bon that was switched OFF and then cancelled must show BOTH
  // reasons — cancelling alone had no `reden` field before, and returning
  // early on `status === geannuleerd` hid the "uitgezet" reason exactly on
  // the overview built to surface it. We show one entry per why.
  const whys = (r) => {
    const out = [];
    if (r.uitgezet) {
      out.push({
        kop: `Uitgezet door ${r.uitgezet.changed_by_name || "onbekend"}`,
        at: r.uitgezet.changed_at,
        reden: r.uitgezet.reden,
      });
    }
    if (r.status === "geannuleerd" && r.annulering) {
      out.push({
        kop: `Geannuleerd door ${r.annulering.door_naam || "onbekend"}`,
        at: r.annulering.at,
        reden: r.annulering.reden,
      });
    }
    return out;
  };

  return (
    <Page console={{ variant: "view", title: "Uitgezet — uren die niemand factureert", facts,
      backSlot: (
        <Button variant="ghost" size="icon" data-testid="uitgezet-back-button" className="-ml-2 text-white hover:bg-console-diep hover:text-white" onClick={() => navigate("/werkbonnen")}>
          <ArrowLeft size={18} />
        </Button>
      ) }}>
      <div className="space-y-4">
        {error && !loading ? (
          <ErrorState testid="uitgezet-load-error" message={error} onRetry={load} />
        ) : loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => <Card key={i} className="p-4"><Skeleton className="h-14 w-full" /></Card>)}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            testid="uitgezet-empty"
            title="Niets uitgezet"
            purpose="Hier staat elke werkbon met geboekte uren die niet gefactureerd wordt of geannuleerd is — met wie dat besloot, wanneer, en waarom. Leeg is precies goed."
          />
        ) : (
          <div className="space-y-2" data-testid="uitgezet-list">
            {rows.map((r) => {
              const ws = whys(r);
              return (
                <Card key={r.id} data-testid={`uitgezet-row-${r.id}`} className="cursor-pointer p-4 transition-colors hover:bg-accent" onClick={() => navigate(`/werkbonnen/${r.id}`)}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Code>{r.werkbon_number}</Code>
                        <WerkbonStatusBadge status={r.status} />
                      </div>
                      <div className="mt-1 text-body text-foreground">{r.omschrijving}</div>
                      {r.location_name && <div className="text-data text-muted-foreground">{r.location_name}</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-mono tabular-nums text-kop text-foreground">{formatUren(r.uren_totaal)} uur</div>
                      {r.bedrag_totaal != null && r.bedrag_totaal !== 0 && (
                        <div className="font-mono tabular-nums text-data text-muted-foreground">{formatEuro(r.bedrag_totaal)}</div>
                      )}
                      {r.tarief_ontbreekt && (
                        <div className="text-data text-muted-foreground">tarief nog niet ingesteld</div>
                      )}
                    </div>
                  </div>
                  {ws.length > 0 && (
                    <ol className="mt-2 space-y-1 border-t border-lijn-zacht pt-2" data-testid={`uitgezet-whys-${r.id}`}>
                      {ws.map((w, i) => (
                        <li key={i} className="text-data text-inkt-zacht">
                          {w.kop}{w.at ? ` op ${formatNlDate((w.at || "").slice(0, 10))}` : ""}
                          {w.reden ? ` — "${w.reden}"` : ""}
                        </li>
                      ))}
                    </ol>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Page>
  );
}
